import { fieldConfigs } from './fields';

export type ConfigValue = string | number | boolean;
export type ConfigMap = Map<string, ConfigValue>;

/**
 * Initialize config with default values from fieldConfigs
 */
export function initializeConfig(): ConfigMap {
  return fieldConfigs.reduce<ConfigMap>((acc, field) => {
    if (field.defaultValue !== undefined) {
      acc.set(field.register, field.defaultValue);
    }
    return acc;
  }, new Map<string, ConfigValue>());
}

/**
 * Prepares configuration commands for UART/CLI transmission
 * Outputs an array of command lines
 */
export function prepareConfigCommands(config: ConfigMap): string[] {
  const commands: string[] = ['# Configuration generated at ' + new Date().toISOString()];

  config.forEach((value, register) => {
    const formattedValue = formatValue(value);
    commands.push(`SET_REGISTER ${register} ${formattedValue}`);
  });

  commands.push('APPLY_CONFIG');
  return commands;
}

/**
 * Formats a value for CLI transmission
 */
function formatValue(value: ConfigValue): string {
  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }
  return String(value);
}

/**
 * Exports configuration as a text file content
 * Same as prepareConfigCommands but returns joined string
 */
export function exportConfigAsText(config: ConfigMap): string {
  return prepareConfigCommands(config).join('\n');
}

/**
 * Imports configuration from text string
 * Expected format: SET_REGISTER <name> <value> per line
 */
export function importConfigFromText(text: string): ConfigMap {
  const lines = text.split('\n').filter(line => line.trim() && !line.startsWith('#'));

  return lines.reduce<ConfigMap>((acc, line) => {
    const match = line.match(/^SET_REGISTER\s+(\S+)\s+(.+)$/);
    if (match) {
      const [, register, valueStr] = match;
      const value = parseValue(valueStr);
      acc.set(register, value);
    }
    return acc;
  }, new Map<string, ConfigValue>());
}

/**
 * Parse a string value back to ConfigValue
 */
function parseValue(value: string): ConfigValue {
  if (value === '1') return true;
  if (value === '0') return false;
  const num = Number(value);
  return Number.isNaN(num) ? value : num;
}
